import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "./components/AdminLayout";
import { message } from "antd";
import axios from "axios";
import "./AdminBanner.css";

const AdminBanners = () => {
  const imageRef = useRef(null);

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    image: null,
    link: "",
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  function handleChange(e) {
    const { name, value, files } = e.target;
    if (name === "image") {
      setForm((prev) => ({ ...prev, image: files?.[0] || null }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  }

  // ✅ Handles new DB style "/banners/xxx.png" + old style "xxx.png"
  const normalizeBannerUrl = (imgValue) => {
    const v = String(imgValue || "").trim();
    if (!v) return "";
    if (v.startsWith("http://") || v.startsWith("https://")) return v;
    if (v.startsWith("/")) return v; // "/banners/xxx.png"
    return `/banners/${v.replace(/^banners[\\/]/, "")}`; // "xxx.png"
  };

  async function getBanners() {
    try {
      setLoading(true);
      const res = await axios.get("/api/banner/get-banners", {
        headers: getAuthHeaders(),
      });

      if (res.data?.success) {
        setBanners(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setBanners([]);
        if (res.data?.message) message.error(res.data.message);
      }
    } catch (error) {
      console.log(error);
      message.error("Failed to fetch banners");
      setBanners([]);
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form?.image) return message.error("Please select banner image");
    if (!form?.link.trim()) return message.error("Please enter a link");

    try {
      const formData = new FormData();
      formData.append("image", form.image);
      formData.append("link", form.link.trim());

      const res = await axios.post("/api/banner/add-banner", formData, {
        headers: {
          ...getAuthHeaders(),
          // ✅ do NOT set Content-Type manually for FormData
        },
      });

      if (res.data?.success) {
        message.success(res.data.message || "Banner uploaded");

        setForm({ image: null, link: "" });
        if (imageRef.current) imageRef.current.value = "";

        getBanners();
      } else {
        message.error(res.data?.message || "Failed to upload banner");
      }
    } catch (error) {
      console.log(error);
      message.error("Failed to upload banner");
    }
  };

  async function handleDeleteBanner(id) {
    const confirmDelete = window.confirm("Are you sure to delete?");
    if (!confirmDelete) return;

    try {
      const res = await axios.post(
        "/api/banner/delete-banner",
        { id },
        { headers: getAuthHeaders() }
      );

      if (res.data?.success) {
        message.success(res.data.message || "Banner deleted");
        // ✅ instant UI update (no reload)
        setBanners((prev) => prev.filter((b) => b._id !== id));
      } else {
        message.error(res.data?.message || "Failed to delete banner");
      }
    } catch (error) {
      console.log(error);
      message.error("Failed to delete banner");
    }
  }

  useEffect(() => {
    getBanners();
  }, []);

  return (
    <AdminLayout>
      <div className="admin-users">
        <div className="page-title">
          <h3 className="m-0">Banners</h3>
        </div>
        <hr />

        <div className="noti-container">
          <h5 className="text-dark mb-3">Home Banners</h5>

          {/* ✅ Form */}
          <form className="banner-form" onSubmit={handleSubmit}>
            <div className="banner-field">
              <small className="text-dark">Banner Image</small>
              <input
                type="file"
                name="image"
                accept=".jpg,.jpeg,.png,.webp"
                className="form-control"
                onChange={handleChange}
                ref={imageRef}
              />
            </div>

            <div className="banner-field">
              <small className="text-dark">Enter Link</small>
              <input
                type="text"
                name="link"
                className="form-control"
                placeholder="Enter link"
                onChange={handleChange}
                value={form.link}
              />
            </div>

            <button type="submit" className="banner-btn">
              Submit
            </button>
          </form>

          {/* ✅ Toolbar */}
          <div className="banner-toolbar">
            <div className="banner-count">
              {loading ? "Loading..." : `Total: ${banners.length}`}
            </div>
            <button
              type="button"
              className="banner-btn outline"
              onClick={getBanners}
              disabled={loading}
            >
              Refresh
            </button>
          </div>

          {/* ✅ Desktop table */}
          <div className="table-wrap">
            <table className="table table-bordered admin-banner-table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Sr No</th>
                  <th style={{ width: 160 }}>Banner</th>
                  <th>Link</th>
                  <th style={{ width: 110 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {!loading && banners.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center">
                      No banners found
                    </td>
                  </tr>
                ) : (
                  banners.map((item, index) => {
                    const url = normalizeBannerUrl(item?.image || item?.imageUrl);
                    return (
                      <tr key={item._id || index}>
                        <td>{index + 1}</td>
                        <td>
                          {url ? (
                            <img
                              className="banner-thumb"
                              src={url}
                              alt="banner"
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="path-cell">{item?.link || "-"}</td>
                        <td>
                          <button
                            type="button"
                            onClick={() => handleDeleteBanner(item?._id)}
                            className="btn btn-danger btn-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ✅ Mobile cards */}
          <div className="banner-cards">
            {banners.map((item) => {
              const url = normalizeBannerUrl(item?.image || item?.imageUrl);
              return (
                <div className="banner-card" key={item._id}>
                  <div className="banner-card-left">
                    {url ? (
                      <img
                        className="banner-card-img"
                        src={url}
                        alt="banner"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="banner-card-img placeholder">No Image</div>
                    )}
                  </div>

                  <div className="banner-card-mid">
                    <div className="banner-card-title">Banner</div>
                    <div className="banner-card-sub">{item?.link || "-"}</div>
                  </div>

                  <div className="banner-card-right">
                    <button
                      type="button"
                      onClick={() => handleDeleteBanner(item?._id)}
                      className="btn btn-danger btn-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <hr className="text-dark my-4" />
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBanners;
